// Two-iframe loader with Penpal-based RPC:
// - Launcher iframe renders the button.
// - Panel iframe renders the full chat panel.
// - Communication uses Penpal RPC instead of window.postMessage.
// - Parent exposes methods children can call: open/close, requestInit, suggestResize, getViewport, configReady.
// - Parent calls child methods: bootstrap(payload) and onViewport(vp).
//
// Global API:
//   livly.Widget.render({...}) and livly.Widget.destroy()

import {
  WindowMessenger,
  connect,
  type Connection,
  type RemoteProxy
} from "penpal";
import { signedFetch } from "@shared/http";

// Suppress unhandled Penpal MessagePort errors (fired internally during
// handshake when the child iframe is removed or navigates away).
window.addEventListener("unhandledrejection", (e) => {
  const msg = String((e.reason as any)?.message ?? e.reason ?? "");
  if (msg.includes("MessagePort") && msg.includes("not connected")) {
    e.preventDefault();
  }
});

const API_BASE = import.meta.env.VITE_API_URL;
const DEFAULT_WIDGET_URL = import.meta.env.VITE_WIDGET_URL;

// Auto-detect development environment
function getDefaultWidgetUrl(forceDev?: boolean): string {
  // If explicitly in dev mode or detected development environment
  if (forceDev) {
    // Common dev ports
    // In development, use the vite dev server port for the widget
    return "http://localhost:5173/";
  }

  // In production, use CDN
  return DEFAULT_WIDGET_URL;
}

type Theme = {
  primary?: string;
  bg?: string;
  text?: string;
  radius?: string;
};

type RenderOptions = {
  widget_key: string;
  position?: "bottom-right" | "bottom-left";
  theme?: Theme;
  widget_url?: string;
  mock?: boolean;
  dev?: boolean; // Force development mode
  timeout?: number; // Request timeout in milliseconds, default 12000 (12s)
};

type InternalState = {
  // Launcher (button) iframe with its own container
  launcherContainer?: HTMLDivElement;
  launcherWrap?: HTMLDivElement;
  launcherFrame?: HTMLIFrameElement;
  launcherConn?: Connection<ChildRPC>;
  launcherRemote?: RemoteProxy<ChildRPC>;

  // Panel iframe with its own container
  panelContainer?: HTMLDivElement;
  panelWrap?: HTMLDivElement;
  panelFrame?: HTMLIFrameElement;
  panelConn?: Connection<ChildRPC>;
  panelRemote?: RemoteProxy<ChildRPC>;

  widgetOrigin?: string;
  initPayload?: any; // cached init payload
  initLoading?: boolean; // prevent concurrent init requests
  initFailed?: boolean; // permanently failed init
  initError?: unknown; // stored error from failed init
  launcherShown?: boolean; // track if launcher has been shown

  cfg?: {
    widgetUrl: string;
    token: string;
    position: "bottom-right" | "bottom-left";
    theme?: Theme;
    mock: boolean;
    timeout: number;
  };

  // Resize throttle
  resizeRaf?: number;
};

interface ProspectWidgetConfigProps {
  name: string;
  position: "bottom-right" | "bottom-left";
}

const state: InternalState = {};

async function render(opts: RenderOptions): Promise<void> {
  // Idempotent: if containers already exist, do nothing
  if (state.launcherContainer || state.panelContainer) return;

  const cfg = {
    widgetUrl: (opts.widget_url || getDefaultWidgetUrl(opts.dev)).replace(
      /\/+$/,
      "/"
    ),
    token: opts.widget_key,
    position: (opts.position || "bottom-right") as
      | "bottom-right"
      | "bottom-left",
    theme: opts.theme,
    mock: !!opts.mock,
    timeout: opts.timeout || 12000
  };

  if (!cfg.widgetUrl || !cfg.token) {
    console.warn(
      "[widget] Missing required options. Need widget_url and widget_key."
    );
    return;
  }

  // Set config early so handleInitRequest can use it
  state.cfg = cfg;

  try {
    // Ensure configuration (widgetConfig + ambassadorConfig) is fully loaded BEFORE creating iframes
    await handleInitRequest();
  } catch (e) {
    console.error("[widget] Initialization failed. Widget will not render.", e);
    return; // Abort render on failure
  }

  let parsedWidgetConfig: ProspectWidgetConfigProps;
  const { prospectWidgetConfig = "" } = state.initPayload || {};
  try {
    parsedWidgetConfig = JSON.parse(prospectWidgetConfig || "{}");
  } catch (e) {
    console.error("[widget] Failed to parse prospectWidgetConfig", e);
    parsedWidgetConfig = { name: "Chat", position: "bottom-right" };
  }

  ensureStyles();

  const widgetOrigin = originOf(cfg.widgetUrl);
  // Launcher container in page corner
  const launcherContainer = document.createElement("div");
  launcherContainer.className = `livly-widget-launcher livly-pos-${parsedWidgetConfig.position}`;
  launcherContainer.setAttribute("data-widget-launcher-container", "true");

  // Panel container in page corner (above launcher)
  const panelContainer = document.createElement("div");
  panelContainer.className = `livly-widget-panel livly-pos-${parsedWidgetConfig.position}`;
  panelContainer.setAttribute("data-widget-panel-container", "true");

  // Launcher iframe (compact)
  const launcherWrap = document.createElement("div");
  launcherWrap.className = "livly-widget-launcher-wrap";

  const launcherFrame = document.createElement("iframe");
  launcherFrame.title = "Chat Widget Button";
  launcherFrame.className = "livly-widget-iframe";
  launcherFrame.setAttribute(
    "sandbox",
    "allow-scripts allow-forms allow-same-origin allow-popups allow-top-navigation allow-top-navigation-by-user-activation"
  );
  launcherFrame.src = buildUrl(cfg.widgetUrl, { view: "launcher" });

  launcherWrap.appendChild(launcherFrame);
  launcherContainer.appendChild(launcherWrap);

  // Panel iframe
  const panelWrap = document.createElement("div");
  panelWrap.className = "livly-widget-panel-inner";
  panelWrap.style.borderRadius = cfg.theme?.radius || "12px";

  const panelFrame = document.createElement("iframe");
  panelFrame.title = "Chat Widget Panel";
  panelFrame.className = "livly-widget-iframe";
  panelFrame.style.pointerEvents = "auto"; // Force enable
  panelFrame.style.touchAction = "manipulation";
  panelFrame.setAttribute(
    "sandbox",
    "allow-scripts allow-forms allow-same-origin allow-popups  allow-top-navigation allow-top-navigation-by-user-activation"
  );
  panelFrame.src = buildUrl(cfg.widgetUrl, { view: "panel" });

  panelWrap.appendChild(panelFrame);
  panelContainer.appendChild(panelWrap);

  document.body.appendChild(launcherContainer);
  document.body.appendChild(panelContainer);

  state.launcherContainer = launcherContainer;
  state.launcherWrap = launcherWrap;
  state.launcherFrame = launcherFrame;
  state.panelContainer = panelContainer;
  state.panelWrap = panelWrap;
  state.panelFrame = panelFrame;
  state.widgetOrigin = widgetOrigin;

  window.addEventListener("resize", onWindowResize);

  // Establish Penpal connections once frames load
  onceLoaded(launcherFrame, () => connectChild("launcher"));
  onceLoaded(panelFrame, () => connectChild("panel"));
}

// RPC interfaces
type Viewport = { width: number; height: number; dpr: number };
type BootstrapPayload = {
  parentOrigin: string;
  apiBase: string;
  themeOverride?: Theme;
  position: "bottom-right" | "bottom-left";
  mock: boolean;
  frame: "launcher" | "panel";
  viewport: Viewport;
};

type ChildRPC = {
  // Called by parent:
  bootstrap(payload: BootstrapPayload): void;
  onViewport(viewport: Viewport): void;
};

type ParentRPC = {
  // Called by child:
  openPanel(): void;
  closePanel(): void;
  requestInit(): Promise<ReturnType<typeof withCommonInit>>;
  suggestResize(
    frame: "launcher" | "panel",
    width: number,
    height: number
  ): void;
  getViewport(): Viewport;
  configReady(): void;
};

function connectChild(which: "launcher" | "panel") {
  const iframe = which === "launcher" ? state.launcherFrame : state.panelFrame;
  if (!iframe || !iframe.contentWindow) {
    console.warn(
      `[widget] ${which} iframe contentWindow unavailable, skipping connect`
    );
    return;
  }

  // Verify iframe is still in the DOM
  if (!document.body.contains(iframe)) {
    console.warn(`[widget] ${which} iframe no longer in DOM, skipping connect`);
    return;
  }

  let messenger: WindowMessenger;
  try {
    messenger = new WindowMessenger({
      remoteWindow: iframe.contentWindow,
      allowedOrigins: ["*"]
    });
  } catch (e) {
    console.warn(`[widget] Failed to create messenger for ${which}:`, e);
    return;
  }

  const conn = connect<ChildRPC>({
    messenger,
    methods: buildParentRPC()
  });

  if (which === "launcher") state.launcherConn = conn;
  else state.panelConn = conn;

  conn.promise
    .then((remote: RemoteProxy<ChildRPC>) => {
      // Verify iframe is still valid before sending bootstrap
      const currentFrame =
        which === "launcher" ? state.launcherFrame : state.panelFrame;
      if (!currentFrame || !document.body.contains(currentFrame)) {
        console.warn(
          `[widget] ${which} iframe removed before bootstrap, skipping`
        );
        return;
      }

      if (which === "launcher") state.launcherRemote = remote;
      else state.panelRemote = remote;

      // Send bootstrap with error handling
      const bootstrapResult = remote.bootstrap({
        parentOrigin: window.location.origin,
        apiBase: API_BASE,
        themeOverride: state.cfg?.theme,
        position: state.cfg?.position || "bottom-right",
        mock: !!state.cfg?.mock,
        frame: which,
        viewport: getViewportPayload()
      });

      // Handle Promise rejections from Penpal RPC to avoid unhandled rejections
      if (
        bootstrapResult &&
        typeof (bootstrapResult as any).catch === "function"
      ) {
        (bootstrapResult as Promise<unknown>).catch(
          (bootstrapError: unknown) => {
            const currentFrameAfterBootstrap =
              which === "launcher" ? state.launcherFrame : state.panelFrame;
            if (
              !currentFrameAfterBootstrap ||
              !document.body.contains(currentFrameAfterBootstrap)
            ) {
              console.debug(
                `[widget] Bootstrap aborted for ${which} (iframe removed)`
              );
              return;
            }
            console.warn(
              `[widget] Failed to send bootstrap to ${which}:`,
              bootstrapError
            );
          }
        );
      }
    })
    .catch((e: unknown) => {
      // Suppress errors when iframe is no longer available (user navigated away)
      const currentFrame =
        which === "launcher" ? state.launcherFrame : state.panelFrame;
      if (!currentFrame || !document.body.contains(currentFrame)) {
        console.debug(
          `[widget] Penpal connect aborted for ${which} (iframe removed)`
        );
        return;
      }
      console.error("[widget] Penpal connect failed", e);
    });
}

function buildParentRPC(): ParentRPC {
  return {
    openPanel,
    closePanel,
    requestInit: async () => {
      try {
        await handleInitRequest();
        // handleInitRequest triggers and caches; return current payload
        if (!state.initPayload) {
          throw new Error("Failed to load widget configuration");
        }
        return withCommonInit(state.initPayload);
      } catch (error) {
        console.error("[widget] requestInit failed:", error);
        throw error; // Re-throw so child components can handle it
      }
    },
    suggestResize: (frame, width, height) => {
      const wrap = frame === "launcher" ? state.launcherWrap : state.panelWrap;
      if (wrap) {
        if (typeof width === "number") wrap.style.width = `${width}px`;
        if (typeof height === "number") wrap.style.height = `${height}px`;
      }
    },
    getViewport: getViewportPayload,
    configReady: showLauncher
  };
}

function showLauncher() {
  if (!state.launcherContainer || state.launcherShown) return;

  state.launcherShown = true;
  state.launcherContainer.classList.add("livly-widget-visible");
}

function openPanel() {
  if (!state.panelContainer) return;

  // Hide launcher first, then show panel
  if (state.launcherContainer) {
    state.launcherContainer.classList.remove("livly-widget-visible");
  }

  state.panelContainer.classList.add("livly-widget-visible");
  state.panelContainer.style.pointerEvents = "auto"; // Re-enable interaction when visible
  state.panelFrame?.focus();
}

function closePanel() {
  if (!state.panelContainer) return;

  // Hide panel first
  state.panelContainer.classList.remove("livly-widget-visible");
  state.panelContainer.style.pointerEvents = "none"; // Disable when hidden

  if (state.launcherContainer) {
    state.launcherContainer.classList.add("livly-widget-visible");
    state.launcherFrame?.focus();
  }
}

async function handleInitRequest() {
  if (state.initPayload) {
    // Already successfully loaded
    return;
  }

  if (state.initFailed) {
    // Previous attempt failed permanently; rethrow stored error to stop further processing
    throw state.initError || new Error("Widget init previously failed");
  }

  if (state.initLoading) {
    // Already loading - wait for it to complete (either success or failure)
    while (state.initLoading && !state.initPayload && !state.initFailed) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    if (state.initFailed)
      throw state.initError || new Error("Widget init previously failed");
    return;
  }

  if (!state.cfg) return; // No config => cannot init

  state.initLoading = true;

  try {
    let payload: any;
    if (state.cfg.mock) {
      payload = {
        sessionToken: "mock-session-token",
        ttlSec: 1800,
        list: [
          {
            id: "michael",
            name: "Mike Scott",
            subtitle: "Prison, puns",
            meta: "2 bed"
          },
          {
            id: "dwight",
            name: "Dwight Schrute",
            subtitle: "Beets, bears",
            meta: "1 bed"
          },
          {
            id: "april",
            name: "April Ludgate",
            subtitle: "Deadpan",
            meta: "studio"
          }
        ],
        ambassadors: [
          {
            ambassadorId: 1,
            displayOrder: 1,
            firstName: "Mike",
            lastName: "Scott",
            hobbies: "Prison, puns",
            profilePictureUrl: "",
            unitNumber: "2B",
            unitType: "2 bedroom",
            hasPets: false,
            hasVehicles: true,
            timeInBuilding: "2 years"
          }
        ],
        propertyName: "Mock Property",
        themeDefaults: {
          primary: "#2563eb",
          bg: "#ffffff",
          text: "#0f172a",
          radius: "12px"
        }
      };
    } else {
      // Example signing against external API endpoints. Adjust API_BASE as needed.
      const timeout = state.cfg?.timeout || 12000; // Use configured timeout

      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("API request timeout")), timeout)
      );

      const widgetConfigPromise = signedFetch(
        new URL(
          `/api/ProspectWidget/${state.cfg.token}/config`,
          API_BASE
        ).toString(),
        {
          method: "GET",
          widgetKey: state.cfg.token
        }
      );

      const ambassadorConfigPromise = signedFetch(
        new URL(
          `/api/ProspectWidget/${state.cfg.token}/ambassadors`,
          API_BASE
        ).toString(),
        {
          method: "GET",
          widgetKey: state.cfg.token
        }
      );

      // Race requests against timeout
      const [widgetConfig, ambassadorConfig] = (await Promise.race([
        Promise.all([widgetConfigPromise, ambassadorConfigPromise]),
        timeoutPromise
      ])) as [Response, Response];

      if (!widgetConfig.ok) {
        // Throw instead of returning so we mark initFailed and do not refetch endlessly
        const body = await safeJson(widgetConfig);
        console.warn(`[widget] init widgetConfig failed`, body);
        throw new Error(
          `widgetConfig request failed: ${widgetConfig.status} ${widgetConfig.statusText}`
        );
      }

      if (!ambassadorConfig.ok) {
        // Throw instead of returning so we mark initFailed and do not refetch endlessly
        const body = await safeJson(ambassadorConfig);
        console.warn("[widget] init ambassadorConfig failed", body);
        throw new Error(
          `ambassadorConfig request failed: ${ambassadorConfig.status} ${ambassadorConfig.statusText}`
        );
      }

      const config = await widgetConfig.json();
      const residents = await ambassadorConfig.json();

      payload = {
        ...config.data,
        ...residents.data
      };
    }

    // Validate payload has required data before considering it successful
    if (!payload || !payload.ambassadors || payload.ambassadors.length === 0) {
      throw new Error("Invalid payload: missing ambassador data");
    }

    state.initPayload = payload;
  } catch (e) {
    console.error("[widget] init error", e);
    // Mark as permanently failed so we don't keep retrying
    state.initFailed = true;
    state.initError = e;
    // Re-throw so child can decide what to do (it should avoid calling again)
    throw e;
  } finally {
    state.initLoading = false;
  }
  // Child will retrieve via RPC when it calls requestInit
}

function withCommonInit(payload: any) {
  return {
    sessionToken: payload.sessionToken,
    ttlSec: payload.ttlSec,
    themeDefaults: payload.themeDefaults,
    themeOverride: state.cfg?.theme,
    apiBase: API_BASE,
    parentOrigin: window.location.origin,
    ...payload
  };
}

// Send viewport to children via RPC

function onWindowResize() {
  if (state.resizeRaf) return;
  state.resizeRaf = window.requestAnimationFrame(() => {
    state.resizeRaf && window.cancelAnimationFrame(state.resizeRaf);
    state.resizeRaf = undefined;
    const vp = getViewportPayload();
    try {
      state.launcherRemote?.onViewport(vp)?.catch?.(() => {});
    } catch {}
    try {
      state.panelRemote?.onViewport(vp)?.catch?.(() => {});
    } catch {}
  });
}

function getViewportPayload() {
  return {
    width: Math.max(
      0,
      Math.floor(window.innerWidth || document.documentElement.clientWidth || 0)
    ),
    height: Math.max(
      0,
      Math.floor(
        window.innerHeight || document.documentElement.clientHeight || 0
      )
    ),
    dpr: window.devicePixelRatio || 1
  };
}

// postMessage helpers removed (using Penpal)

function onceLoaded(iframe: HTMLIFrameElement, cb: () => void) {
  if (iframe.contentDocument?.readyState === "complete") {
    // Add a delay to ensure the iframe's JavaScript has time to set up listeners
    setTimeout(cb, 100);
  } else {
    iframe.addEventListener(
      "load",
      () => {
        // Add a delay to ensure the iframe's JavaScript has time to set up listeners
        setTimeout(cb, 100);
      },
      { once: true }
    );
  }
}

function originOf(url: string) {
  return new URL(url, window.location.href).origin;
}

function buildUrl(base: string, query: Record<string, string>) {
  const u = new URL(base, window.location.href);
  Object.entries(query).forEach(([k, v]) => u.searchParams.set(k, v));
  return u.toString();
}

function safeJson(r: Response) {
  return r
    .clone()
    .json()
    .catch(() => undefined);
}

// Inject shared stylesheet once
function ensureStyles() {
  if (document.getElementById("livly-widget-styles")) return;
  const style = document.createElement("style");
  style.id = "livly-widget-styles";
  style.textContent = `/* Livly Resident Ambassador Widget Loader Styles */
  .livly-widget-launcher, .livly-widget-panel { position:fixed; opacity:0; transform:translateY(100vh); transition: hover .5s ease-in-out; }
  .livly-widget-launcher { z-index:2147483000; bottom:16px; width:400px; height:200px; }
  .livly-widget-panel { z-index:2147483001; bottom:10px; width:400px; pointer-events:none; }
  .livly-widget-launcher-wrap { width:100%; height:100%; }
  .livly-widget-panel-inner { width:400px; height:calc(100vh - 200px); max-width:400px; max-height:calc(100vh - 200px); overflow:hidden; box-shadow:0 10px 30px rgba(2,6,23,0.2); background:#fff; display:flex; flex-direction:column; }
  .livly-widget-iframe { width:100%; height:100%; border:0; display:block; }
  .livly-widget-visible { opacity:1; transform:translateY(0); }
  .livly-pos-bottom-right { right:16px; }
  .livly-pos-bottom-left { left:16px; }

  /* Mobile / small viewport adjustments */
  @media (max-width: 425px), (max-height: 425px) {
   
    .livly-widget-panel { 
      top:0; left:0 !important; right:0 !important; bottom:0 !important; 
      width:100vw; height:100dvh; 
      margin:0; 
      transition:opacity .35s ease, transform .35s ease; 
    }
    .livly-widget-panel-inner { 
      width:100% !important; height:100% !important; max-width:100%; max-height:100%; 
      border-radius:0 !important; box-shadow:none !important; 
    }
    .livly-widget-launcher.livly-widget-visible { transform:translateY(0); }
    .livly-widget-launcher { width: 48px; height: 48px;}
  }

  /* Respect theme radius variable */
  .livly-widget-panel-inner { border-radius: var(--widget-radius, 12px); }
  `;
  document.head.appendChild(style);
}

function destroy() {
  window.removeEventListener("resize", onWindowResize);
  if (state.launcherContainer) state.launcherContainer.remove();
  if (state.panelContainer) state.panelContainer.remove();
  try {
    state.launcherConn?.destroy();
  } catch {}
  try {
    state.panelConn?.destroy();
  } catch {}
  state.launcherContainer = undefined;
  state.launcherWrap = undefined;
  state.launcherFrame = undefined;
  state.launcherConn = undefined;
  state.launcherRemote = undefined;
  state.panelContainer = undefined;
  state.panelWrap = undefined;
  state.panelFrame = undefined;
  state.panelConn = undefined;
  state.panelRemote = undefined;
  state.widgetOrigin = undefined;
  state.initPayload = undefined;
  state.initLoading = undefined;
  state.initFailed = undefined;
  state.initError = undefined;
  state.launcherShown = undefined;
  state.cfg = undefined;
  if (state.resizeRaf) {
    cancelAnimationFrame(state.resizeRaf);
    state.resizeRaf = undefined;
  }
}

// Expose global API
const Widget = { render, destroy };
const g = globalThis as any;
g.livly = g.livly || {};
g.livly.Widget = Widget;

export { Widget };
